// Unpublished Work © 2023-2024 Deere & Company.

import { trace } from "@opentelemetry/api";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";
import { Resource } from "@opentelemetry/resources";
import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { SemanticResourceAttributes } from "@opentelemetry/semantic-conventions";
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  Method,
} from "axios";

const MAX_RETRIES = 2;

const resource = Resource.default().merge(
  new Resource({
    [SemanticResourceAttributes.SERVICE_NAME]: "JDPO-UI",
    [SemanticResourceAttributes.SERVICE_VERSION]: "0.0.0",
  })
);

const provider = new WebTracerProvider({
  resource,
});

provider.register();

trace.setGlobalTracerProvider(provider);

registerInstrumentations({
  instrumentations: [
    new XMLHttpRequestInstrumentation({
      propagateTraceHeaderCorsUrls: [
        /^https:\/\/partsom-.*/,
        /^https:\/\/api-us-.*/,
        /^https:\/\/gateway\.global-it-architecture-.*/,
      ],
    }),
  ],
});

/**
 * @description By default AxiosInstance itself doesn't have [IndexSignature](https://www.typescriptlang.org/docs/handbook/2/indexed-access-types.html)
 *
 * @interface AxiosInstanceWithIndexSignature
 * @extends {AxiosInstance}
 */
export interface AxiosInstanceWithIndexSignature extends AxiosInstance {
  [key: string]: any; // Add index signature
}

export const instance: AxiosInstanceWithIndexSignature = axios.create({
  baseURL: "/api/",
  timeout: 300000,
  headers: { "Content-Type": "application/json" },
});

const is4xxStatusCode = (code: number): boolean => code >= 400 && code < 500;

/**
 * @description Execute Axios request
 *
 * @template T - Type of response data
 * @template R - Type of response
 * @param {Method} action - HTTP method
 * @param {string} url - URL
 * @param {AxiosRequestConfig} [config] - Axios request config
 * @return {*}  {Promise<R>}
 */
export const executeAxiosRequest = async <T = any>(
  axiosInstance: AxiosInstanceWithIndexSignature,
  action: Method,
  url: string,
  config?: AxiosRequestConfig,
  body?: any
): Promise<T> => {
  const transformedAction = action.toLowerCase();

  if (["put", "patch", "post"].includes(transformedAction)) {
    return axiosInstance[transformedAction](url, body, config);
  }

  return axiosInstance[transformedAction](url, config);
};

/**
 * @description Execute Axios request with retries
 *
 * @template T - Type of response data
 * @template R - Type of response
 * @param {string} url - URL
 * @param {AxiosRequestConfig} [config] - Axios request config
 * @param {AxiosInstance} [axiosInstance] - Axios instance
 * @return {*}  {Promise<R>}
 */
export const getWithRetries = async <T = any, R = AxiosResponse<T>>(
  url: string,
  config?: AxiosRequestConfig,
  axiosInstance: AxiosInstanceWithIndexSignature = instance
): Promise<R> => {
  const pRetry = (await import("p-retry")).default;
  return pRetry(() => executeAxiosRequest(axiosInstance, "get", url, config), {
    retries: MAX_RETRIES,
    onFailedAttempt: (error: any) => {
      if (error.response && is4xxStatusCode(error.response.status)) throw error;
      /* istanbul ignore next */
      if (error.retriesLeft === 0) {
        // eslint-disable-next-line no-console
        console.error(
          error.attemptNumber,
          " failed unable to connect with server!"
        );
      }
    },
  });
};

/**
 * @description Execute Axios request with retries
 *
 * @template T - Type of response data
 * @template R - Type of response
 * @param {string} url - URL
 * @param {AxiosRequestConfig} [config] - Axios request config
 * @param {AxiosInstance} [axiosInstance] - Axios instance
 * @return {*}  {Promise<R>}
 */
export const postWithRetries = async <T = any, R = AxiosResponse<T>>(
  url: string,
  body: any,
  config?: AxiosRequestConfig,
  /* istanbul ignore next */
  axiosInstance: AxiosInstanceWithIndexSignature = instance
): Promise<R> => {
  const pRetry = (await import("p-retry")).default;
  return pRetry(
    () => executeAxiosRequest(axiosInstance, "post", url, config, body),
    {
      retries: MAX_RETRIES,
      onFailedAttempt: (error: any) => {
        if (error.response && is4xxStatusCode(error.response.status))
          throw error;
        /* istanbul ignore next */
        if (error.retriesLeft === 0) {
          // eslint-disable-next-line no-console
          console.error(
            error.attemptNumber,
            " failed unable to connect with server!"
          );
        }
      },
    }
  );
};

/**
 * @description Execute Axios request with retries
 *
 * @template T - Type of response data
 * @template R - Type of response
 * @param {string} url - URL
 * @param {AxiosRequestConfig} [config] - Axios request config
 * @param {AxiosInstance} [axiosInstance] - Axios instance
 * @return {*}  {Promise<R>}
 */
export const patchWithRetries = async <T = any, R = AxiosResponse<T>>(
  url: string,
  body: any,
  config?: AxiosRequestConfig,
  /* istanbul ignore next */
  axiosInstance: AxiosInstanceWithIndexSignature = instance
): Promise<R> => {
  const pRetry = (await import("p-retry")).default;
  return pRetry(
    () => executeAxiosRequest(axiosInstance, "patch", url, config, body),
    {
      retries: MAX_RETRIES,
      onFailedAttempt: (error: any) => {
        if (error.response && is4xxStatusCode(error.response.status))
          throw error;
        /* istanbul ignore next */
        if (error.retriesLeft === 0) {
          // eslint-disable-next-line no-console
          console.error(
            error.attemptNumber,
            " failed unable to connect with server!"
          );
        }
      },
    }
  );
};

export const putWithRetries = async <T = any, R = AxiosResponse<T>>(
  url: string,
  body: any,
  config?: AxiosRequestConfig,
  /* istanbul ignore next */
  axiosInstance: AxiosInstanceWithIndexSignature = instance
): Promise<R> => {
  const pRetry = (await import("p-retry")).default;
  return pRetry(
    () => executeAxiosRequest(axiosInstance, "put", url, config, body),
    {
      retries: MAX_RETRIES,
      onFailedAttempt: (error: any) => {
        if (error.response && is4xxStatusCode(error.response.status))
          throw error;
        /* istanbul ignore next */
        if (error.retriesLeft === 0) {
          // eslint-disable-next-line no-console
          console.error(
            error.attemptNumber,
            " failed unable to connect with server!"
          );
        }
      },
    }
  );
};

export const deleteWithRetries = async <T = any, R = AxiosResponse<T>>(
  url: string,
  config?: AxiosRequestConfig,
  axiosInstance: AxiosInstanceWithIndexSignature = instance
): Promise<R> => {
  const pRetry = (await import("p-retry")).default;
  return pRetry(
    () => executeAxiosRequest(axiosInstance, "delete", url, config),
    {
      retries: MAX_RETRIES,
      onFailedAttempt: (error: any) => {
        if (error.response && is4xxStatusCode(error.response.status))
          throw error;
        /* istanbul ignore next */
        if (error.retriesLeft === 0) {
          // eslint-disable-next-line no-console
          console.error(
            error.attemptNumber,
            " failed unable to connect with server!"
          );
        }
      },
    }
  );
};

export interface AxiosInstanceWithRetries
  extends AxiosInstanceWithIndexSignature {
  getWithRetries: typeof getWithRetries;
  postWithRetries: typeof postWithRetries;
  patchWithRetries: typeof patchWithRetries;
  putWithRetries: typeof putWithRetries;
  deleteWithRetries: typeof deleteWithRetries;
}

instance.getWithRetries = getWithRetries;
instance.postWithRetries = postWithRetries;
instance.patchWithRetries = patchWithRetries;
instance.putWithRetries = putWithRetries;
instance.deleteWithRetries = deleteWithRetries;

export default instance as AxiosInstanceWithRetries;
